import React, {
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react'

const Dialog = forwardRef(
  (
    {
      modal = false,
      children,
      onClose,
      onOpen,
      style = {},
      className = '',
      closeOnOutsideClick = true,
      closeOnEscape = true,
      position = null,
      offset = 8,
      ...props
    },
    ref
  ) => {
    const dialogRef = useRef(null)

    useImperativeHandle(ref, () => ({
      open: (positionElement) => openDialog(positionElement),
      close: closeDialog,
      toggle: (positionElement) =>
        isOpen() ? closeDialog() : openDialog(positionElement),
      isOpen: isOpen
    }))

    const isOpen = () => dialogRef.current?.open || false

    const openDialog = (positionElement = null) => {
      const dialog = dialogRef.current
      if (!dialog) {
        return
      }

      if (modal) {
        dialog.showModal()
      } else {
        dialog.show()

        const targetElement = positionElement || position?.relativeTo
        if (targetElement) {
          positionDialog(targetElement)
        }
      }

      onOpen?.()
    }

    const closeDialog = () => {
      const dialog = dialogRef.current
      if (dialog && dialog.open) {
        dialog.close()
        onClose?.()
      }
    }

    const positionDialog = (targetElement) => {
      if (!targetElement || !dialogRef.current) return

      const dialog = dialogRef.current
      const rect = targetElement.getBoundingClientRect()
      const placement = position?.placement || 'bottom-right'

      dialog.style.position = 'absolute'
      dialog.style.top = 'auto'
      dialog.style.left = 'auto'
      dialog.style.right = 'auto'
      dialog.style.bottom = 'auto'

      switch (placement) {
        case 'bottom-right':
          dialog.style.top = `${rect.bottom + offset}px`
          dialog.style.left = `${rect.right - dialog.offsetWidth}px`
          break
        case 'bottom-left':
          dialog.style.top = `${rect.bottom + offset}px`
          dialog.style.left = `${rect.left}px`
          break
        case 'top-right':
          dialog.style.bottom = `${window.innerHeight - rect.top + offset}px`
          dialog.style.left = `${rect.right - dialog.offsetWidth}px`
          break
        case 'top-left':
          dialog.style.bottom = `${window.innerHeight - rect.top + offset}px`
          dialog.style.left = `${rect.left}px`
          break
        case 'right':
          dialog.style.top = `${rect.top}px`
          dialog.style.left = `${rect.right + offset}px`
          break
        case 'left':
          dialog.style.top = `${rect.top}px`
          dialog.style.right = `${window.innerWidth - rect.left + offset}px`
          break
        default:
          dialog.style.top = `${rect.bottom + offset}px`
          dialog.style.left = `${
            rect.left + rect.width / 2 - dialog.offsetWidth / 2
          }px`
      }

      adjustForViewport(dialog)
    }

    const adjustForViewport = (dialog) => {
      const dialogRect = dialog.getBoundingClientRect()

      if (dialogRect.right > window.innerWidth) {
        dialog.style.left = `${window.innerWidth - dialogRect.width - 10}px`
      }
      if (dialogRect.left < 0) {
        dialog.style.left = '10px'
      }

      if (dialogRect.bottom > window.innerHeight) {
        dialog.style.top = `${window.innerHeight - dialogRect.height - 10}px`
      }
      if (dialogRect.top < 0) {
        dialog.style.top = '10px'
      }
    }

    useEffect(() => {
      if (!closeOnOutsideClick || modal) return

      const handleClickOutside = (e) => {
        const dialog = dialogRef.current
        if (!dialog || !dialog.open) return

        const rect = dialog.getBoundingClientRect()
        const isOutside =
          e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom

        if (isOutside) {
          closeDialog()
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside)
      }
    }, [closeOnOutsideClick, modal])

    useEffect(() => {
      if (!closeOnEscape) return

      const handleEscape = (e) => {
        if (e.key === 'Escape' && dialogRef.current?.open) {
          closeDialog()
        }
      }

      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }, [closeOnEscape])

    const defaultStyle = {
      padding: 0,
      border: 'none',
      backgroundColor: 'transparent',
      outline: 'none',
      // For modal dialogs, let the browser handle centering
      // We'll override with the style prop if needed
      ...style
    }

    // Apply backdrop and centering styles for modal dialogs
    useEffect(() => {
      if (!modal) return

      // Apply styles immediately for modal dialogs
      const styleId = 'dialog-backdrop-styles'
      let styleElement = document.getElementById(styleId)

      if (!styleElement) {
        styleElement = document.createElement('style')
        styleElement.id = styleId
        document.head.appendChild(styleElement)
      }

      styleElement.textContent = `
        dialog[open] {
          display: flex;
          flex-direction: column;
          animation: dialogFadeIn 0.15s ease-out;
        }
        dialog::backdrop {
          background-color: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(3px);
          animation: backdropFadeIn 0.15s ease-out;
        }
        /* Use browser's default centering for modal */
        dialog:modal {
          margin: auto;
          max-width: min(600px, 90vw);
          max-height: 80vh;
        }
        /* Prevent focus flickering */
        dialog[open] *:focus {
          outline: none;
        }
        dialog[open] *:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 2px;
        }
        @keyframes dialogFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes backdropFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `

      return () => {
        // Cleanup if needed
      }
    }, [modal])

    return (
      <dialog
        ref={dialogRef}
        className={className}
        style={defaultStyle}
        onClick={(e) => {
          e.stopPropagation()
        }}
        {...props}
      >
        {children}
      </dialog>
    )
  }
)

Dialog.displayName = 'Dialog'

export default Dialog
